<template>
    <div class="calendar-events">
        <div v-for="(offer, index) in offersComputed" :key="index">
            <div v-if="offer.slots.length" class="calendar-events__offer" @click="toggleOfferSlotPreview(offer)">
                <div>
                    <span class="calendar-events__title">
                        <div class="calendar-events__title--icon">
                            <common-company-avatar
                                v-if="getOffer(offer)?.companyName"
                                :company="{ photoAvatar: getOffer(offer)?.avatar }"
                                size="micro"
                            ></common-company-avatar>
                            <common-user-avatar
                                v-else
                                :user="{ photoAvatar: getOffer(offer)?.avatar }"
                                size="small"
                            ></common-user-avatar>
                        </div>
                        <div class="calendar-events__title--text">
                            {{ getOffer(offer)?.offerTitleTextContent?.originalText }}
                            <div :class="eventsNumberClass(offer?.slots.filter((item) => item.calendarEventId).length)">
                                {{ offer?.slots.filter((item) => item.calendarEventId).length }}
                            </div>
                        </div>
                    </span>
                    <br />
                    <span class="calendar-events__address">
                        {{ getOffer(offer)?.availabilityTitleTextContent.originalText }}
                    </span>
                    <br v-if="getOffer(offer)?.companyName" />
                    <span v-if="getOffer(offer)?.companyName" class="calendar-events__company">
                        {{ $t('common.microcopy.company') }}: {{ getOffer(offer)?.companyName }}
                    </span>
                    <br />
                    <span class="calendar-events__address">{{ getOffer(offer)?.address }}, {{ getOffer(offer)?.city }}</span>
                </div>
                <div class="calendar-events__offer--slots-action">
                    <common-icon :name="offer.isPreviewSlotOpen ? 'arrow-up' : 'arrow-down'" size="medium"></common-icon>
                </div>
            </div>

            <Transition name="fade" mode="in-out">
                <div v-if="offer.isPreviewSlotOpen" class="calendar-events__content">
                    <div v-for="(slot, indexSlots) in offer.slots" :key="indexSlots" class="calendar-events__badge">
                        <common-badge
                            :value="offer.from"
                            :title="getSlotTime(slot, offer)"
                            :type="getBadgeType(slot)"
                            icon="dots"
                            :secondaryIcon="showSecondaryIcon(slot)"
                            @badge-click="slotClicked(getUniqueSlotName(index, indexSlots))"
                            @badge-click-secondary="secondarySlotClicked(getUniqueSlotName(index, indexSlots))"
                        ></common-badge>

                        <common-dropdown
                            v-click-away="closeSecondaryDropdown"
                            class="calendar-events__badge--dropdown"
                            :isOpen="isSecondarySlotSelected(getUniqueSlotName(index, indexSlots))"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <div class="calendar-events__preview">
                                <p>{{ slot.firstName }} {{ slot.lastName }}</p>
                                <p class="calendar-events__preview--description">{{ slot.description }}</p>
                                <p v-if="slot.allowOverlapAfterMinutes">
                                    {{ $t('pages.calendar.my_calendar.shorten_reservation') }}
                                </p>
                            </div>
                        </common-dropdown>

                        <common-dropdown
                            :id="`x${index}y${indexSlots}`"
                            v-click-away="closeDropdown"
                            class="calendar-events__badge--dropdown"
                            :isOpen="isSlotSelected(getUniqueSlotName(index, indexSlots))"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <div class="calendar-events__actions">
                                <div :class="reservationStatusClass(slot)">{{ getReservationStatus(slot) }}</div>
                                <button
                                    v-if="slot.status && slot.status !== ReservationStatusEnum.UNAVAILABLE"
                                    @click="goToReservation(slot)"
                                >
                                    {{ $t('components.reservations.reservation_status.actions.VIEW') }}
                                </button>
                                <button
                                    v-for="action in getActions(slot)"
                                    :key="action"
                                    @click="goToReservation(slot, action, offer)"
                                >
                                    {{ $t(`components.reservations.reservation_status.actions.${action}`) }}
                                </button>
                            </div>
                        </common-dropdown>
                    </div>
                </div>
            </Transition>
        </div>
        <div v-if="!hasOffersSlot" class="calendar-events__message">
            <span>{{ $t('pages.calendar.my_calendar.no_slots_in_past') }}</span>
        </div>
    </div>
</template>

<script>
    import availabilityTransformer from 'transformers/availabilityTransformer'
    import ReservationService from 'services/ReservationsService'
    import ReservationStatusEnum from 'enums/reservation_status.enum'
    import ReservationActionEnum from 'enums/reservation_action.enum'
    import RoutesEnum from 'enums/routes.enum'
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import cloneDeep from 'lodash.clonedeep'

    const isEqual = require('lodash.isequal')

    export default {
        props: {
            offers: { type: Array, default: new Array() },
            selectedDate: { type: String, default: '' },
            availabilityOffers: { type: Array, default: new Array() },
        },

        data: () => ({
            selectedSlot: null,
            secondarySelectedSlot: null,
            ReservationStatusEnum: ReservationStatusEnum,
            calendarEvent: null,
            offersComputed: [],
            hasOffersSlot: true,
            openedSlotsIds: [],
        }),

        computed: {},

        methods: {
            getSlotTime(slot, offer) {
                return `${availabilityTransformer.minutesDateToLocalTZTime(
                    slot.from,
                    this.selectedDate,
                    offer.timeZone,
                )} - ${availabilityTransformer.minutesDateToLocalTZTime(
                    slot.allowOverlapAfterMinutes ? slot.from + slot.allowOverlapAfterMinutes : slot.to,
                    this.selectedDate,
                    offer.timeZone,
                )}`
            },

            showSecondaryIcon(slot) {
                return slot.status && slot.status !== ReservationStatusEnum.UNAVAILABLE ? 'eye' : undefined
            },

            getBadgeType(slot) {
                if (!slot?.status) {
                    return 'secondary'
                }
                return ReservationStatusEnum[slot.status].toLowerCase()
            },

            getOffer(offer) {
                return this.availabilityOffers?.find((item) => item.availabilityOfferId === offer?.availabilityOfferId)
            },

            getUniqueSlotName(offerIndex, slotIndex) {
                return `x${offerIndex}y${slotIndex}`
            },

            slotClicked(slot) {
                if (this.isSlotSelected(slot)) {
                    return (this.selectedSlot = null)
                }
                setTimeout(() => {
                    const el = document.getElementById(slot)
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }, 100)
                this.selectedSlot = slot
            },

            secondarySlotClicked(slot) {
                if (this.isSecondarySlotSelected(slot)) {
                    return (this.secondarySelectedSlot = null)
                }

                this.secondarySelectedSlot = slot
            },

            isSecondarySlotSelected(slot) {
                return isEqual(this.secondarySelectedSlot, slot)
            },

            isSlotSelected(slot) {
                return isEqual(this.selectedSlot, slot)
            },

            closeSecondaryDropdown() {
                this.secondarySelectedSlot = null
                this.calendarEvent = null
            },

            closeDropdown() {
                this.selectedSlot = null
            },

            getReservationStatus(slot) {
                if (!slot.status) {
                    return this.$t('common.microcopy.open').toUpperCase()
                }
                return this.$t(`components.reservations.reservation_status.statuses.${slot.status}`).toUpperCase()
            },

            reservationStatusClass(slot) {
                return {
                    'calendar-events__actions-status': true,
                    [`calendar-events__actions-status--${slot.status ?? 'empty'}`]: true,
                }
            },

            getActions(slot) {
                return ReservationService.getReservationActionsByStatus(
                    slot.status,
                    true,
                    this.isEventInPast(slot),
                    this.isEventInMoreThanOneMinute(slot),
                    this.isEventInMoreThan20Minutes(slot),
                )
            },

            getCalendarEvent(slot) {
                CalendarEventAPI.getById(slot.calendarEventId).then((res) => {
                    slot = res
                })
            },
            isEventInMoreThanOneMinute(slot) {
                const minutesDifference = Math.floor((this.eventStartTimestamp(slot) - new Date()) / 60000)
                return minutesDifference >= 1 ? true : false
            },
            isEventInMoreThan20Minutes(slot) {
                const minutesDifference = Math.floor((this.eventStartTimestamp(slot) - new Date()) / 60000)
                return minutesDifference >= 20 ? true : false
            },
            eventStartTimestamp(slot) {
                const selectedDateTime = new Date(this.selectedDate)
                selectedDateTime.setHours(1, 0, 0, 0)
                return new Date(selectedDateTime.setMinutes(selectedDateTime.getMinutes() + slot.from))
            },
            isEventInPast(slot) {
                return new Date() > this.eventStartTimestamp(slot)
            },
            goToReservation(slot, action, offer) {
                if (action === ReservationActionEnum.CANCEL && slot.status === ReservationStatusEnum.UNAVAILABLE) {
                    CalendarEventAPI.changeReservationStatus({
                        calendarEventId: Number(slot.calendarEventId),
                        status: ReservationStatusEnum.CANCELED,
                    }).then(() => {
                        slot.calendarEventId = undefined
                        slot.status = undefined
                    })
                    return
                }

                if (action === ReservationActionEnum.MAKE_UNAVAILABLE) {
                    CalendarEventAPI.makeSlotUnavailable(this.selectedDate, offer.availabilityOfferId, slot.from)
                        .then((res) => {
                            slot.status = ReservationStatusEnum.UNAVAILABLE
                            slot.calendarEventId = res.id
                            this.emitEvent({ name: 'status-changed', payload: slot.status })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('pages.calendar.my_calendar.unavailable_slot'))
                        })
                }

                if (action === ReservationActionEnum.NO_SHOW || action === ReservationActionEnum.DONE) {
                    CalendarEventAPI.changeReservationStatus({
                        calendarEventId: Number(slot.calendarEventId),
                        status: action,
                    }).then(() => {
                        slot.status =
                            action === ReservationActionEnum.NO_SHOW ? ReservationStatusEnum.NO_SHOW : ReservationStatusEnum.DONE
                        this.emitEvent({ name: 'status-changed', payload: slot.status })
                        this.$toast.success(this.$t('pages.calendar.my_calendar.status_successfully_changed'))
                    })
                    return
                }
                if (!slot.calendarEventId) {
                    return
                }
                this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id: slot.calendarEventId } })
            },

            toggleOfferSlotPreview(offer) {
                offer.isPreviewSlotOpen = !offer.isPreviewSlotOpen

                if (offer.isPreviewSlotOpen) {
                    if (!this.openedSlotsIds.includes(offer.availabilityOfferId)) {
                        this.openedSlotsIds.push(offer.availabilityOfferId)
                    }
                } else {
                    this.openedSlotsIds = this.openedSlotsIds.filter((id) => id !== offer.availabilityOfferId)
                }
            },

            eventsNumberClass(val) {
                return {
                    'calendar-events__title--events-number': true,
                    'calendar-events__title--events-number--zero': !val,
                }
            },

            getOffers() {
                this.offersComputed = cloneDeep(this.offers).map((item) => {
                    return {
                        ...item,
                        isPreviewSlotOpen: this.offers.length < 5 || this.openedSlotsIds.includes(item.availabilityOfferId),
                    }
                })
                this.hasOffersSlot = this.offersComputed.some((offer) => offer.slots.length > 0)
            },
        },

        watch: {
            offers() {
                this.getOffers()
            },
        },

        mounted() {
            this.getOffers()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/calendar/_calendar-events.scss';
</style>
